import { Input } from '@crowdex/ui/components/input';
import type { PullRequestState, PullRequestSummary } from '@crowdex/workspace-protocol';
import {
  failedPullRequestChecks,
  type PullRequestCockpit,
  pendingPullRequestChecks,
} from '@crowdex/workspace-protocol/pull-request-cockpit';
import { GitMerge, GitPullRequest, Link2, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { SummaryButtonRow } from '@/components/desktop-summary/summary-primitives';
import {
  conversationPullRequestList,
  isDismissedPullRequest,
  isPullRequestUrl,
  linkConversationPullRequest,
  removeConversationPullRequest,
  useConversationPullRequests,
} from '@/features/pull-request/conversation-pull-requests';
import { pullRequestStateClass } from '@/features/pull-request/pull-request';
import { errorMessage } from '@/lib/error-message';

const STATE_WORD: Record<PullRequestState, string> = {
  draft: 'draft',
  open: 'open',
  merged: 'merged',
  closed: 'closed',
};

const CHECKS_WORD = {
  failing: 'checks failing',
  pending: 'checks running',
  passing: 'checks passed',
};
const REVIEW_WORD = {
  approved: 'approved',
  changes_requested: 'changes requested',
  review_required: 'review required',
  unknown: null,
};

/** How long Undo stays after attaching or removing a pull request. */
const UNDO_MS = 8_000;

/** "5 of 6 checks": the checks that passed, of those the pull request runs. */
export function checksLabel(cockpit: PullRequestCockpit): string | null {
  const total = cockpit.checks.length;
  if (total === 0) return null;
  const passed =
    total - failedPullRequestChecks(cockpit).length - pendingPullRequestChecks(cockpit).length;
  return `${passed} of ${total} ${total === 1 ? 'check' : 'checks'}`;
}

/** A pull request with what its cockpit knows of it (reviews, mergeability, size). */
function withCockpit(
  pullRequest: PullRequestSummary,
  cockpit: PullRequestCockpit | null,
): PullRequestSummary {
  if (cockpit === null) return pullRequest;
  return {
    ...pullRequest,
    mergeability: cockpit.mergeable,
    reviewDecision: cockpit.reviewDecision,
    additions: cockpit.additions,
    deletions: cockpit.deletions,
  };
}

/** "PR #556 · open · 5 of 6 checks · approved": what a pull request's row says. */
export function pullRequestRowLabel(
  pullRequest: PullRequestSummary,
  cockpit: PullRequestCockpit | null,
): string {
  const checks =
    cockpit !== null
      ? checksLabel(cockpit)
      : pullRequest.checks === undefined
        ? null
        : CHECKS_WORD[pullRequest.checks];
  const review =
    pullRequest.reviewDecision === undefined ? null : REVIEW_WORD[pullRequest.reviewDecision];
  const open = pullRequest.state === 'open' || pullRequest.state === 'draft';
  const conflicts = open && pullRequest.mergeability === 'conflicting' ? 'conflicts' : null;
  return [`PR #${pullRequest.number}`, STATE_WORD[pullRequest.state], checks, review, conflicts]
    .filter(Boolean)
    .join(' · ');
}

function Size({ pullRequest }: { pullRequest: PullRequestSummary }) {
  if (pullRequest.additions === undefined || pullRequest.deletions === undefined) return null;
  return (
    <>
      <span className="text-desktop-success">+{pullRequest.additions}</span>{' '}
      <span className="text-destructive">−{pullRequest.deletions}</span>
    </>
  );
}

const iconButton =
  'flex size-6 shrink-0 items-center justify-center rounded text-muted-foreground transition-colors hover:bg-accent/55 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring [&_svg]:size-3.5';
const textButton =
  'type-meta shrink-0 rounded px-1.5 py-0.5 text-muted-foreground transition-colors hover:bg-accent/55 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring';

/** One pull request: it opens (its panel, or the forge), and the user may remove it. */
function PullRequestRow({
  pullRequest,
  cockpit,
  onOpen,
  onRemove,
  onUndo,
}: {
  pullRequest: PullRequestSummary;
  cockpit: PullRequestCockpit | null;
  onOpen: () => void;
  onRemove?: (() => void) | undefined;
  onUndo?: (() => void) | undefined;
}) {
  const Icon = pullRequest.state === 'merged' ? GitMerge : GitPullRequest;
  const shown = withCockpit(pullRequest, cockpit);
  return (
    <div className="group flex min-w-0 items-center gap-1" data-summary-pull-request>
      <div className="min-w-0 flex-1">
        <SummaryButtonRow
          icon={<Icon className={pullRequestStateClass(pullRequest.state)} />}
          onClick={onOpen}
          title={pullRequest.title}
          trailing={shown.additions === undefined ? undefined : <Size pullRequest={shown} />}
        >
          {pullRequestRowLabel(shown, cockpit)}
        </SummaryButtonRow>
      </div>
      {onUndo !== undefined ? (
        <button className={textButton} onClick={onUndo} type="button">
          Undo
        </button>
      ) : (
        onRemove !== undefined && (
          <button
            aria-label={`Remove pull request #${pullRequest.number}`}
            className={`${iconButton} opacity-0 focus-visible:opacity-100 group-hover:opacity-100`}
            onClick={onRemove}
            title="Remove"
            type="button"
          >
            <X />
          </button>
        )
      )}
    </div>
  );
}

/** "Link a pull request": its URL, pasted, checked by the server against the folder's repository. */
function LinkPullRequest({ onLink }: { onLink: (url: string) => Promise<void> }) {
  const [open, setOpen] = useState(false);
  const [url, setUrl] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [working, setWorking] = useState(false);
  if (!open)
    return (
      <SummaryButtonRow icon={<Link2 />} onClick={() => setOpen(true)}>
        <span className="text-muted-foreground">Link a pull request</span>
      </SummaryButtonRow>
    );
  const submit = async () => {
    const value = url.trim();
    if (!isPullRequestUrl(value)) {
      setError('Not a pull request URL');
      return;
    }
    setWorking(true);
    try {
      await onLink(value);
      setOpen(false);
      setUrl('');
      setError(null);
    } catch (cause) {
      setError(errorMessage(cause));
    } finally {
      setWorking(false);
    }
  };
  return (
    <form
      className="grid gap-1 py-1"
      onSubmit={(event) => {
        event.preventDefault();
        void submit();
      }}
    >
      <Input
        aria-label="Pull request URL"
        autoFocus
        className="type-ui h-7"
        disabled={working}
        onChange={(event) => {
          setUrl(event.target.value);
          setError(null);
        }}
        onKeyDown={(event) => {
          if (event.key !== 'Escape') return;
          setOpen(false);
          setError(null);
        }}
        placeholder="Pull request URL"
        value={url}
      />
      {error !== null && (
        <p className="type-meta text-destructive" role="alert">
          {error}
        </p>
      )}
    </form>
  );
}

const openUrl = (url: string) => window.open(url, '_blank', 'noopener,noreferrer');

/**
 * The conversation's pull requests in its Code section: its branch's one (it opens the Pull
 * request tool), then those bound to it (opened, printed by a tool call, of a branch it pushed,
 * linked; each opens on its forge, each can be removed), the folder's branch's one it did not
 * make as "Existing pull request · Attach", and "Link a pull request". Undo follows an attach or
 * a removal a while. Without a conversation of this machine (`conversationId` null), its
 * branch's one alone.
 */
export function SummaryPullRequests({
  conversationId,
  visible,
  primary,
  cockpit,
  onOpenPrimary,
}: {
  conversationId: string | null;
  visible: boolean;
  /** Its branch's pull request, the Pull request tool's. */
  primary: PullRequestSummary | null;
  cockpit: PullRequestCockpit | null;
  onOpenPrimary: () => void;
}) {
  const view = useConversationPullRequests(conversationId, visible);
  const [undo, setUndo] = useState<{ kind: 'attached' | 'removed'; url: string } | null>(null);
  useEffect(() => {
    if (undo === null) return;
    const timer = setTimeout(() => setUndo(null), UNDO_MS);
    return () => clearTimeout(timer);
  }, [undo]);
  const shown =
    conversationId === null
      ? primary === null
        ? []
        : [primary]
      : conversationPullRequestList(primary, view);
  const run = (action: Promise<void>, next: typeof undo) => {
    void action.then(
      () => setUndo(next),
      () => setUndo(null),
    );
  };
  const offer =
    view?.offer !== null &&
    view?.offer !== undefined &&
    !isDismissedPullRequest(view, view.offer.url) &&
    !shown.some((entry) => entry.url === view.offer?.url)
      ? view.offer
      : null;
  const removed =
    undo?.kind === 'removed' && !shown.some((entry) => entry.url === undo.url) ? undo : null;
  return (
    <>
      {shown.map((pullRequest) => {
        const isPrimary = primary !== null && pullRequest.url === primary.url;
        return (
          <PullRequestRow
            cockpit={isPrimary ? cockpit : null}
            key={pullRequest.url}
            onOpen={isPrimary ? onOpenPrimary : () => openUrl(pullRequest.url)}
            onRemove={
              conversationId === null
                ? undefined
                : () =>
                    run(removeConversationPullRequest(conversationId, pullRequest.url), {
                      kind: 'removed',
                      url: pullRequest.url,
                    })
            }
            onUndo={
              conversationId !== null && undo?.kind === 'attached' && undo.url === pullRequest.url
                ? () => run(removeConversationPullRequest(conversationId, pullRequest.url), null)
                : undefined
            }
            pullRequest={pullRequest}
          />
        );
      })}
      {conversationId !== null && removed !== null && (
        <div className="flex min-w-0 items-center gap-1">
          <div className="min-w-0 flex-1">
            <SummaryButtonRow icon={<GitPullRequest />}>
              <span className="text-muted-foreground">Pull request removed</span>
            </SummaryButtonRow>
          </div>
          <button
            className={textButton}
            onClick={() => run(linkConversationPullRequest(conversationId, removed.url), null)}
            type="button"
          >
            Undo
          </button>
        </div>
      )}
      {conversationId !== null && offer !== null && (
        <div className="flex min-w-0 items-center gap-1" data-summary-pull-request-offer>
          <div className="min-w-0 flex-1">
            <SummaryButtonRow
              icon={<GitPullRequest />}
              onClick={() => openUrl(offer.url)}
              title={offer.title}
            >
              <span className="text-muted-foreground">Existing pull request</span> #{offer.number}
            </SummaryButtonRow>
          </div>
          <button
            className={textButton}
            onClick={() =>
              run(linkConversationPullRequest(conversationId, offer.url), {
                kind: 'attached',
                url: offer.url,
              })
            }
            type="button"
          >
            Attach
          </button>
          <button
            aria-label={`Dismiss pull request #${offer.number}`}
            className={iconButton}
            onClick={() => run(removeConversationPullRequest(conversationId, offer.url), null)}
            title="Dismiss"
            type="button"
          >
            <X />
          </button>
        </div>
      )}
      {conversationId !== null && (
        <LinkPullRequest onLink={(url) => linkConversationPullRequest(conversationId, url)} />
      )}
    </>
  );
}
